import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';
import * as Sentry from "@sentry/react";
import { 
  ArrowRight, 
  ArrowLeft, 
  X, 
  ChevronDown,
  ChevronUp,
  Calendar,
  AlertTriangle
} from 'lucide-react';

// Date validation constants
const SERVICE_START_DATE = '2025-01-01';

// Helper function to get current date in YYYY-MM-DD format
const getCurrentDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Helper function to validate date
const validateDate = (dateString) => {
  if (!dateString) return { valid: true };
  
  const date = new Date(dateString + 'T00:00:00');
  const minDate = new Date(SERVICE_START_DATE + 'T00:00:00');
  const maxDate = new Date(getCurrentDate() + 'T00:00:00');
  
  if (isNaN(date.getTime())) {
    return { valid: false, error: 'Invalid date format' };
  }
  
  if (date < minDate) {
    return { valid: false, error: `Date cannot be before ${SERVICE_START_DATE}` };
  }
  
  if (date > maxDate) {
    return { valid: false, error: 'Date cannot be in the future' };
  }
  
  return { valid: true };
};

// Helper function to validate date range
const validateDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return { valid: true };
  
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  
  if (start > end) {
    return { valid: false, error: 'Start date must be less than or equal to end date' };
  }
  
  return { valid: true };
};

// Sentry logging helpers
export const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'transactions' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'transactions' },
      extra: context 
    });
  }
};

export const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'transactions',
    level: 'info',
    data: context
  });
};

export const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

export const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// Network error detection with 6-position priority
export const getNetworkErrorMessage = (error) => {
  // Position 2.1: Browser knows it's offline
  if (!navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  // Position 2.2: Request timeout
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return {
      message: "Request timed out. Please try again.",
      code: 'TIMEOUT'
    };
  }
  
  // Position 2.3: Network error but browser thinks it's online
  if (error.code === 'ERR_NETWORK' && navigator.onLine) {
    return {
      message: "Unable to reach server. Please try later.",
      code: 'SERVER_UNREACHABLE'
    };
  }
  
  // Position 2.4: Network error and browser now offline
  if (error.code === 'ERR_NETWORK' && !navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  // Position 2.5: Request sent but no response received
  if (error.request && !error.response) {
    return {
      message: "No response from server. Please try again.",
      code: 'NO_RESPONSE'
    };
  }
  
  // Position 2.6: Fallback
  return {
    message: "Something went wrong. Please try again.",
    code: 'UNKNOWN_NETWORK_ERROR'
  };
};
// Centralized currency utility
export const currencyUtils = {
  getSymbol: (currencyCode) => {
    switch(currencyCode) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'INR': return '₹';
      default: return '$'; // Default to USD symbol
    }
  },
  
  // Default currency for the application
  defaultCurrency: 'USD'
};

// Safe number parsing
export const safeParseFloat = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  // Make sure strings like "0.0075" are properly parsed
  const parsed = parseFloat(value);
  // Handle potential precision issues by returning the exact value for known small amounts
  if (value === "0.0075" || value === "-0.0075") {
    return parseFloat(value); // Ensure we keep exact value
  }
  return isNaN(parsed) ? 0 : parsed;
};

// Responsive utilities
export const useResponsive = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 640);
      setIsTablet(width >= 640 && width < 1024);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getGridClasses = () => {
    if (isMobile) return 'grid grid-cols-1 gap-3';
    if (isTablet) return 'grid grid-cols-2 gap-4';
    return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6';
  };

  const getStatsCardClasses = () => {
    if (isMobile) return 'grid grid-cols-2 gap-2';
    if (isTablet) return 'grid grid-cols-4 gap-3';
    return 'grid grid-cols-1 md:grid-cols-4 gap-6';
  };

  return { isMobile, isTablet, getGridClasses, getStatsCardClasses };
};

export function formatTransactionType(type) {
  switch (type) {
    case 'credit':
      return 'Credit Purchase';
    case 'volume_usage_hour':
      return 'Volume Usage';
    case 'snapshot_usage_hour':
      return 'Snapshot Usage';
    case 'server_usage_hour':
      return 'Server Usage';
    case 'manual_credit':
      return 'Admin';
    case 'manual_debit':
      return 'Admin deduct' ; 
    default:
      return type;
  }
}

export const formatDate = (dateString, currency = currencyUtils.defaultCurrency) => {
  if (!dateString) return 'N/A';
  
  // Create date object from string
  const date = new Date(dateString);
  
  // If USD account, subtract 9 hours and 30 minutes
  if (currency === 'USD') {
    // Create a new date by subtracting 9 hours and 30 minutes
    const adjustedDate = new Date(date.getTime() - (9 * 60 + 30) * 60 * 1000);
    return adjustedDate.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  
  // For INR, EUR, or other currencies, return as is
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const StatusBadge = ({ status }) => {
  let statusClass = '';
  let bgColor = '';
  let textColor = '';
  let borderColor = '';
  
if (['completed', 'active', 'success'].includes(status.toLowerCase())) {
  bgColor = '#1c2b22';
  textColor = '#e0eacc';
  borderColor = '#2a3c32';
} else if (['hibernated'].includes(status.toLowerCase())) {
  bgColor = 'rgba(70, 130, 180, 0.3)';
  textColor = '#87CEEB';
  borderColor = '#4682B4';
} else if (['failed', 'error', 'cancelled'].includes(status.toLowerCase())) {
  bgColor = 'rgba(156, 50, 50, 0.3)';
  textColor = '#ff9494';
  borderColor = '#9c3232';
} else if (['pending', 'processing'].includes(status.toLowerCase())) {
  bgColor = 'rgba(156, 130, 50, 0.3)';
  textColor = '#ffda94';
  borderColor = '#9c8232';
}
  
  return (
    <span 
      style={{
        backgroundColor: bgColor,
        color: textColor,
        border: `1px solid ${borderColor}`,
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: '500',
        display: 'inline-block'
      }}
    >
      {status}
    </span>
  );
};

export const AmountDisplay = ({ amount, currency = currencyUtils.defaultCurrency, transaction_type = '' }) => {
  const parsedAmount = safeParseFloat(amount);
  
  // Check if this is a debit transaction type
  const isDebitType = 
    transaction_type === 'usage_minute' || 
    transaction_type === 'volume_usage_hour' ||
    transaction_type === 'snapshot_usage_hour' ||
    transaction_type === 'server_usage_hour' ||
    (transaction_type && typeof transaction_type === 'string' && transaction_type.includes('cost'));
                     
  // Force negative display for debit types
  const isNegative = isDebitType ? true : parsedAmount < 0;
  const symbol = currencyUtils.getSymbol(currency);
  
  // Use 4 decimal places for small amounts
  const displayAmount = Math.abs(parsedAmount) < 0.01 
    ? Math.abs(parsedAmount).toFixed(4) 
    : Math.abs(parsedAmount).toFixed(2);
    
  return (
    <span style={{ 
      color: isNegative ? '#ff9494' : '#e0eacc',
      fontWeight: '500'
    }}>
      {isNegative ? '-' : '+'}{symbol}{displayAmount}
    </span>
  );
};

export const DateRangeDropdown = ({ dateRange, setDateRange, applyFilters }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState('');
  const [selectedEndDate, setSelectedEndDate] = useState('');
  const [dateValidationError, setDateValidationError] = useState('');
  const dropdownRef = useRef(null);
  
  useEffect(() => {
    // If dateRange is set from outside, update local state
    if (dateRange.startDate && dateRange.endDate) {
      setSelectedStartDate(dateRange.startDate);
      setSelectedEndDate(dateRange.endDate);
    }
  }, [dateRange]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    
    logUserAction('date_range_dropdown_toggled', {
      isOpen: !isOpen,
      hasDateRange: !!(dateRange.startDate && dateRange.endDate)
    });
  };
  
  const formatDisplayDate = () => {
    if (dateRange.startDate && dateRange.endDate) {
      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
    }
    return 'Select date range';
  };
  
  const handlePresetSelect = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    
    // Format dates as YYYY-MM-DD
    const endFormatted = end.toISOString().split('T')[0];
    const startFormatted = start.toISOString().split('T')[0];
    
    setDateRange({
      startDate: startFormatted,
      endDate: endFormatted
    });
    
    setSelectedStartDate(startFormatted);
    setSelectedEndDate(endFormatted);
    
    logUserAction('date_range_preset_selected', {
      days,
      startDate: startFormatted,
      endDate: endFormatted
    });
    
    // Auto apply filters
    applyFilters({
      startDate: startFormatted,
      endDate: endFormatted
    });
    
    setIsOpen(false);
  };
  
  const handleDateChange = (type, value) => {
    // Clear previous errors
    setDateValidationError('');
    
    // Validate the changed date
    const dateValidation = validateDate(value);
    if (!dateValidation.valid) {
      setDateValidationError(dateValidation.error);
      return;
    }
    
    // Update the appropriate date
    if (type === 'start') {
      setSelectedStartDate(value);
      
      // Validate range if end date is also set
      if (selectedEndDate) {
        const rangeValidation = validateDateRange(value, selectedEndDate);
        if (!rangeValidation.valid) {
          setDateValidationError(rangeValidation.error);
          return;
        }
      }
    } else {
      setSelectedEndDate(value);
      
      // Validate range if start date is also set
      if (selectedStartDate) {
        const rangeValidation = validateDateRange(selectedStartDate, value);
        if (!rangeValidation.valid) {
          setDateValidationError(rangeValidation.error);
          return;
        }
      }
    }
    
    logUserAction(`date_range_${type}_changed`, { date: value });
  };
  
  const handleCustomRangeApply = () => {
    // Validate dates before applying
    const startDateValidation = validateDate(selectedStartDate);
    if (!startDateValidation.valid) {
      setDateValidationError(startDateValidation.error);
      return;
    }
    
    const endDateValidation = validateDate(selectedEndDate);
    if (!endDateValidation.valid) {
      setDateValidationError(endDateValidation.error);
      return;
    }
    
    const rangeValidation = validateDateRange(selectedStartDate, selectedEndDate);
    if (!rangeValidation.valid) {
      setDateValidationError(rangeValidation.error);
      return;
    }
    
    // Clear validation errors
    setDateValidationError('');
    
    if (selectedStartDate && selectedEndDate) {
      setDateRange({
        startDate: selectedStartDate,
        endDate: selectedEndDate
      });
      
      logUserAction('date_range_custom_applied', {
        startDate: selectedStartDate,
        endDate: selectedEndDate
      });
      
      // Auto apply filters
      applyFilters({
        startDate: selectedStartDate,
        endDate: selectedEndDate
      });
      
      setIsOpen(false);
    }
  };
  
  const clearDateRange = (e) => {
    e.stopPropagation();
    setDateRange({ startDate: '', endDate: '' });
    setSelectedStartDate('');
    setSelectedEndDate('');
    setDateValidationError('');
    
    logUserAction('date_range_cleared');
    
    // Auto apply filters (clear them)
    applyFilters({ startDate: '', endDate: '' });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex items-center">
        <div
          onClick={toggleDropdown}
          className="cursor-pointer flex items-center border border-[#333333] rounded-md bg-[#1d1d1d] p-2"
          style={{
            height: '37.6px',
            fontSize: '0.875rem',
            width: '100%'
          }}
        >
          <Calendar size={16} className="text-gray-400 mr-2" />
          <span>{formatDisplayDate()}</span>
          <ChevronDown size={16} className="text-gray-400 ml-auto" />
        </div>
        {(dateRange.startDate || dateRange.endDate) && (
          <button
            onClick={clearDateRange}
            className="ml-2 text-gray-400 hover:text-white p-1 rounded-full hover:bg-[#333333]"
          >
            <X size={16} />
          </button>
        )}
      </div>
      
      {isOpen && (
        <div 
          className="absolute z-50 mt-1 bg-[#1d1d1d] border border-[#333333] rounded-md shadow-lg p-4"
          style={{ width: '300px', right: 0 }}
        >
          <div className="flex gap-2 mb-4 text-sm">
            <button 
              className={`px-2 py-1 rounded ${!customMode ? 'bg-[#e0eacc] text-black' : 'bg-[#333333] text-white hover:bg-[#444444]'}`}
              onClick={() => {
                setCustomMode(false);
                setDateValidationError('');
                logUserAction('date_range_mode_changed', { mode: 'presets' });
              }}
            >
              Presets
            </button>
            <button 
              className={`px-2 py-1 rounded ${customMode ? 'bg-[#e0eacc] text-black' : 'bg-[#333333] text-white hover:bg-[#444444]'}`}
              onClick={() => {
                setCustomMode(true);
                setDateValidationError('');
                logUserAction('date_range_mode_changed', { mode: 'custom' });
              }}
            >
              Custom
            </button>
          </div>
          
          {!customMode ? (
            <div className="flex flex-col space-y-2">
              <button 
                className="text-left px-3 py-2 rounded hover:bg-[#333333] text-white"
                onClick={() => handlePresetSelect(7)}
              >
                Last 7 days
              </button>
              <button 
                className="text-left px-3 py-2 rounded hover:bg-[#333333] text-white"
                onClick={() => handlePresetSelect(30)}
              >
                Last 30 days
              </button>
              <button 
                className="text-left px-3 py-2 rounded hover:bg-[#333333] text-white"
                onClick={() => handlePresetSelect(90)}
              >
                Last 90 days
              </button>
              <button 
                className="text-left px-3 py-2 rounded hover:bg-[#333333] text-white"
                onClick={() => {
                  const now = new Date();
                  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                  
                  const startFormatted = startOfMonth.toISOString().split('T')[0];
                  const endFormatted = now.toISOString().split('T')[0];
                  
                  setDateRange({
                    startDate: startFormatted,
                    endDate: endFormatted
                  });
                  
                  logUserAction('date_range_preset_selected', {
                    preset: 'this_month',
                    startDate: startFormatted,
                    endDate: endFormatted
                  });
                  
                  // Auto apply filters
                  applyFilters({
                    startDate: startFormatted,
                    endDate: endFormatted
                  });
                  
                  setIsOpen(false);
                }}
              >
                This month
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Date Validation Error */}
              {dateValidationError && (
                <div style={{
                  backgroundColor: 'rgba(238, 85, 85, 0.1)',
                  border: '1px solid rgba(238, 85, 85, 0.3)',
                  borderRadius: '4px',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <AlertTriangle size={14} color="#ff7777" />
                  <span style={{
                    fontSize: '0.75rem',
                    color: '#ff9999'
                  }}>
                    {dateValidationError}
                  </span>
                </div>
              )}
              
              <div>
                <label className="block text-xs text-gray-400 mb-1">From</label>
                <input 
                  type="date" 
                  value={selectedStartDate || ''}
                  min={SERVICE_START_DATE}
                  max={getCurrentDate()}
                  onChange={(e) => handleDateChange('start', e.target.value)}
                  className="w-full bg-[#333333] border border-[#444444] rounded p-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">To</label>
                <input 
                  type="date"
                  value={selectedEndDate || ''}
                  min={SERVICE_START_DATE}
                  max={getCurrentDate()}
                  onChange={(e) => handleDateChange('end', e.target.value)}
                  className="w-full bg-[#333333] border border-[#444444] rounded p-2 text-white text-sm"
                />
              </div>
              <div className="flex justify-end">
                <button 
                  className="px-3 py-1 rounded font-medium text-sm"
                  style={{
                    backgroundColor: dateValidationError ? '#666666' : '#e0eacc',
                    color: dateValidationError ? '#999999' : 'black',
                    cursor: dateValidationError ? 'not-allowed' : 'pointer'
                  }}
                  onClick={handleCustomRangeApply}
                  disabled={!selectedStartDate || !selectedEndDate || !!dateValidationError}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// FIXED: TransactionTypeDropdown component to ensure selections apply properly
export const TransactionTypeDropdown = ({ selectedType, setSelectedType, applyFilters }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Define transaction types with values that match backend expectations
  const transactionTypes = [
    { value: '', label: 'All Types' },
    { value: 'credit', label: 'Credit Purchase' },
    { value: 'volume_usage_hour', label: 'Volume Usage' },
    { value: 'snapshot_usage_hour', label: 'Snapshot Usage' },
    { value: 'server_usage_hour', label: 'Server Usage' }
  ];
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    
    logUserAction('transaction_type_dropdown_toggled', {
      isOpen: !isOpen,
      currentType: selectedType
    });
  };
  
  // FIXED: Updated handleTypeSelect to ensure filter is applied
  const handleTypeSelect = (value) => {
    
    logUserAction('transaction_type_selected', {
      previousType: selectedType,
      newType: value,
      typeLabel: transactionTypes.find(t => t.value === value)?.label
    });
    
    // Update local state
    setSelectedType(value);
    
    // Then explicitly apply the filter with this type value
    applyFilters({ type: value, transaction_type: value });
    
    setIsOpen(false);
  };
  
  const getSelectedTypeLabel = () => {
    const selected = transactionTypes.find(type => type.value === selectedType);
    return selected ? selected.label : 'All Types';
  };
  
  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={toggleDropdown}
        className="cursor-pointer flex items-center justify-between border border-[#333333] rounded-md bg-[#1d1d1d] p-2"
        style={{
          height: '37.6px',
          fontSize: '0.875rem'
        }}
      >
        <span>{getSelectedTypeLabel()}</span>
        <ChevronDown size={16} className="text-gray-400 ml-2" />
      </div>
      
      {isOpen && (
        <div 
          className="absolute z-50 mt-1 bg-[#1d1d1d] border border-[#333333] rounded-md shadow-lg overflow-hidden"
          style={{ width: '100%', maxHeight: '200px', overflowY: 'auto' }}
        >
          {transactionTypes.map(type => (
            <div 
              key={type.value}
              className={`px-3 py-2 cursor-pointer hover:bg-[#333333] ${
                selectedType === type.value ? 'bg-[#252525] text-[#e0eacc]' : 'text-white'
              }`}
              onClick={() => handleTypeSelect(type.value)}
            >
              {type.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const Pagination = ({ currentPage, totalPages, onPageChange, isMobile }) => {
  // Calculate which page numbers to show
  const getPageNumbers = () => {
    const pageNumbers = [];
    
    // Always show first page
    pageNumbers.push(1);
    
    // Calculate range around current page
    const rangeStart = Math.max(2, currentPage - 1);
    const rangeEnd = Math.min(totalPages - 1, currentPage + 1);
    
    // Add ellipsis after first page if needed
    if (rangeStart > 2) {
      pageNumbers.push('...');
    }
    
    // Add pages in range
    for (let i = rangeStart; i <= rangeEnd; i++) {
      pageNumbers.push(i);
    }
    
    // Add ellipsis before last page if needed
    if (rangeEnd < totalPages - 1) {
      pageNumbers.push('...');
    }
    
    // Always show last page if there's more than one page
    if (totalPages > 1) {
      pageNumbers.push(totalPages);
    }
    
    return pageNumbers;
  };

  const pageNumbers = getPageNumbers();
  
  const handlePageChange = (newPage, isNavigation = false) => {
    if (newPage !== currentPage && newPage > 0 && newPage <= totalPages) {
      logUserAction('pagination_page_changed', {
        previousPage: currentPage,
        newPage,
        totalPages,
        isNavigation
      });
      
      onPageChange(newPage, true);
    }
  };
  
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: isMobile ? '4px' : '8px',
      marginTop: '24px',
      marginBottom: '24px',
      flexWrap: 'wrap'
    }}>
      <button 
        style={{
          backgroundColor: '#333333',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          padding: isMobile ? '6px 8px' : '8px 12px',
          fontSize: isMobile ? '0.75rem' : '0.875rem',
          fontWeight: '500',
          cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
          opacity: currentPage <= 1 ? '0.5' : '1',
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '4px' : '6px'
        }}
        onClick={() => handlePageChange(currentPage - 1, true)}
        disabled={currentPage <= 1}
      >
        <ArrowLeft size={isMobile ? 12 : 16} />
        {!isMobile && 'Previous'}
      </button>
      
      {pageNumbers.map((page, index) => (
        page === '...' ? (
          <span 
            key={`ellipsis-${index}`}
            style={{
              color: '#a0a0a0',
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              fontWeight: '500'
            }}
          >
            ...
          </span>
        ) : (
          <button
            key={`page-${page}`}
            style={{
              backgroundColor: currentPage === page ? '#e0eacc' : '#333333',
              color: currentPage === page ? 'black' : 'white',
              border: 'none',
              borderRadius: '6px',
              width: isMobile ? '28px' : '32px',
              height: isMobile ? '28px' : '32px',
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
            onClick={() => handlePageChange(page, false)}
          >
            {page}
          </button>
        )
      ))}
      
      <button 
        style={{
          backgroundColor: '#333333',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          padding: isMobile ? '6px 8px' : '8px 12px',
          fontSize: isMobile ? '0.75rem' : '0.875rem',
          fontWeight: '500',
          cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
          opacity: currentPage >= totalPages ? '0.5' : '1',
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '4px' : '6px'
        }}
        onClick={() => handlePageChange(currentPage + 1, true)}
        disabled={currentPage >= totalPages}
      >
        {!isMobile && 'Next'}
        <ArrowRight size={isMobile ? 12 : 16} />
      </button>
    </div>
  );
};